<template>
  <div class="">
    <vx-card>
      <div class="heading">
        <h4>{{ form_title || "Foreign Remittance" }}</h4>
      </div>

      <div class="flex flex-wrap items-start justify-between">
        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Project <span class="text-danger">*</span></h6>
          <multiselect
            :disabled="Boolean(formData)"
            class="w-full"
            label="project_name"
            track-by="project_id"
            v-model="form.project"
            name="Project Name"
            :options="projectList"
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
            @select="projectSelected"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Project Name')"
            >
              {{ errors.first("Project Name") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Amount Paid From <span class="text-danger">*</span></h6>
          <multiselect
            :disabled="Boolean(formData) || [4, 5].includes(is_var)"
            class="w-full"
            label="alt_org_name"
            value="default_alt_org_id"
            v-model="form.default_alt_org"
            name="Name"
            :options="alternateOrg"
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
            @select="storePaidFrom"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Amount Paid From')"
            >
              {{ errors.first("Amount Paid From") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Vendor <span class="text-danger">*</span></h6>
          <multiselect
            :disabled="
              !form.project || !form.default_alt_org || Boolean(formData)
            "
            class="w-full"
            v-model="form.vendor"
            label="vendor_name"
            track-by="vendor_id"
            :options="vendorList"
            :allow-empty="false"
            name="Vendor Name"
            open-direction="bottom"
            placeholder="Type to search"
            selectLabel=""
            selectedLabel=""
            deselect-label=""
            :loading="searchingVendor"
            :limit="3"
            :internal-search="false"
            @search-change="vendorSearched"
            @select="vendorSelected"
            preserveSearch
          />
          <div class="error-msg text-sm">
            <p class="font-bold" v-if="vendorFR">
              Approved FR Balance Amount:
              <span
                class="font-extrabold"
                :class="
                  vendorFR.FR_balance > 0 ? 'text-success' : 'text-danger'
                "
              >
                {{ (vendorFR.FR_balance || 0).toLocaleString() }}
              </span>
            </p>
            <p>
              <vs-chip
                color="success"
                class="font-extrabold text-black"
                title="Base Approved FR amount"
                v-if="vendorFR && vendorFR.FR_amount"
              >
                {{ (vendorFR.FR_amount.base_amount || 0).toLocaleString() }}
              </vs-chip>
              <vs-chip
                color="warning"
                class="font-extrabold text-black"
                title="Total Approved FR amount"
                v-if="vendorFR && vendorFR.FR_amount"
              >
                {{ (vendorFR.FR_amount.total_amount || 0).toLocaleString() }}
              </vs-chip>
            </p>
            <span
              class="text-danger text-sm"
              v-show="errors.has('Vendor Name')"
            >
              {{ errors.first("Vendor Name") }}
            </span>
          </div>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Invoice Number</h6>
          <vs-input
            autocomplete="off"
            type="text"
            name="Amount"
            v-model="form.invoice_number"
            class="w-full"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Invoice Number')"
            >
              {{ errors.first("Invoice Number") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Invoice Date</h6>
          <Datepicker
            placeholder="From Date"
            format="dd-MM-yyyy"
            v-model="form.invoice_date"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Invoice Date')"
            >
              {{ errors.first("Invoice Date") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Payment Due Date</h6>
          <Datepicker
            placeholder="To Date"
            v-model="form.payment_due_date"
            format="dd-MM-yyyy"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Payment Due Date')"
            >
              {{ errors.first("Payment Due Date") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Category <span class="text-danger">*</span></h6>
          <multiselect
            class="w-full"
            label="category_name"
            track-by="category_id"
            v-model="form.category"
            name="Category Name"
            :options="categoryList"
            searchable
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
            @select="categorySelected"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Category Name')"
            >
              {{ errors.first("Category Name") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Subcategory <span class="text-danger">*</span></h6>
          <multiselect
            class="w-full"
            label="category_name"
            track-by="category_id"
            v-model="form.subcategory"
            name="Subcategory Name"
            :options="subcategoryList"
            searchable
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Subcategory Name')"
            >
              {{ errors.first("Subcategory Name") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Country <span class="text-danger">*</span></h6>
          <multiselect
            class="w-full"
            label="country_name"
            track-by="country_id"
            v-model="form.country"
            name="Priority"
            :options="countryList"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Priority')">
              {{ errors.first("Priority") }}
            </span>
          </p>
        </div>
      </div>

      <h6>Foreign Remittance</h6>
      <div
        class="flex flex-wrap items-center justify-between mb-2 p-5 bg-grey-light rounded-lg"
      >
        <div class="w-full sm:w-1/2 md:w-1/5 p-1">
          <h6>From Currency <span class="text-danger">*</span></h6>
          <multiselect
            class="w-full"
            label="currency_code"
            track-by="currency_id"
            v-model="foreign_remittance.currency"
            :options="currencyList"
            :searchable="true"
            :allow-empty="false"
            placeholder="Select Option"
            open-direction="bottom"
            selectLabel=""
            selectedLabel=""
            deselect-label=""
          />
        </div>
        <div class="w-full sm:w-1/2 md:w-1/5 p-1">
          <h6>To Currency <span class="text-danger">*</span></h6>
          <multiselect
            disabled
            class="w-full"
            label="currency_code"
            track-by="currency_id"
            v-model="foreign_remittance.converted_currency"
            :options="currencyList"
            :searchable="true"
            :allow-empty="false"
            open-direction="bottom"
            placeholder="Select Option"
            selectLabel=""
            selectedLabel=""
            deselect-label=""
          />
        </div>
        <div class="w-full sm:w-1/2 md:w-1/5 p-1">
          <h6>Rate <span class="text-danger">*</span></h6>
          <vs-input
            :disabled="
              !foreign_remittance.currency ||
                !foreign_remittance.converted_currency
            "
            type="number"
            name="Amount"
            v-model="foreign_remittance.rate"
            class="w-full"
          />
        </div>
        <div class="w-full sm:w-1/2 md:w-1/5 p-1">
          <h6>Unit <span class="text-danger">*</span></h6>
          <vs-input
            :disabled="
              !foreign_remittance.currency ||
                !foreign_remittance.converted_currency
            "
            type="number"
            name="Amount"
            v-model="foreign_remittance.unit"
            class="w-full"
          />
        </div>
        <div class="w-full sm:w-1/2 md:w-1/5 p-1">
          <h6>Conversion Value</h6>
          <vs-input
            disabled
            autocomplete="off"
            type="number"
            name="Amount"
            v-model="foreign_remittance.converted_value"
            class="w-full"
          />
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between">
        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Amount</h6>
          <vs-input
            disabled
            class="w-full"
            type="number"
            name="Amount"
            v-model="form.amount"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Amount')">
              {{ errors.first("Amount") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Mode of payment</h6>
          <multiselect
            class="w-full"
            label="payment_mode_name"
            track-by="payment_mode_id"
            v-model="form.payment_mode"
            name="Mode of payment"
            :options="paymentModeList"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
          />
          <p class="error-msg text-sm">
            <span
              class="text-danger text-sm"
              v-show="errors.has('Mode of payment')"
            >
              {{ errors.first("Mode of payment") }}
            </span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Priority</h6>
          <multiselect
            class="w-full"
            label="priority_name"
            track-by="priority_id"
            v-model="form.priority"
            name="Priority"
            :options="priorityList"
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
          />
          <p class="error-msg text-sm">
            <span class="text-danger text-sm" v-show="errors.has('Priority')">{{
              errors.first("Priority")
            }}</span>
          </p>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Tax</h6>
          <div class="row">
            <div class="col-6">
              <multiselect
                :disabled="!form.amount || [4, 5].includes(is_var)"
                class="w-full"
                label="gst_name"
                track-by="gst_id"
                v-model="form.gst"
                :options="gstList"
                :allow-empty="false"
                open-direction="bottom"
                placeholder="Select Option"
                deselect-label=""
                @select="calculateGST"
              />
            </div>
            <div class="col-6">
              <vs-input
                :disabled="!form.gst || form.gst.gst_name != 'Manual Amount'"
                class="w-full"
                type="number"
                name="GST"
                v-model="form.gst_amount"
              />
              <p class="error-msg text-sm"></p>
            </div>
          </div>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Total Amount</h6>
          <vs-input
            class="w-full"
            type="number"
            v-model="form.total_amount"
            disabled
          />
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Set Name</h6>
          <multiselect
            class="w-full"
            label="location_name"
            track-by="location_id"
            v-model="form.shootLocation"
            :options="shootLocationList"
            searchable
            allow-empty
            open-direction="bottom"
            placeholder="Select Option"
            deselect-label="Can't remove this value"
          />
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 p-1">
          <h6>Comment</h6>
          <vs-textarea
            class="w-full"
            name="Comment"
            v-model="form.comment"
            rows="5"
          />
        </div>

        <div class="w-full sm:w-1/2 md:w-2/3 p-1">
          <h6>Upload Document <span class="text-danger">*</span></h6>
          <vue-dropzone
            class="rounded"
            id="dropzone"
            ref="myVueDropzoneFile"
            :options="dropzoneOptionsFiles"
            @vdropzone-success-multiple="vdropzoneSuccessFile"
            @vdropzone-removed-file="vremoved"
            @vdropzone-error-multiple="vdropzoneError"
            @vdropzone-complete-multiple="vdropzoneComplete"
            @vdropzone-added-files="vdropzoneFilesAdded"
            destroyDropzone
            duplicateCheck
          />
          <vs-button
            v-if="fileUpload.deleteButtonStatus"
            type="filled"
            @click.prevent="clearFile()"
            class="feather icon-trash mt-2"
          />
          <vs-button
            v-if="fileUpload.deleteButtonStatus"
            type="filled"
            @click.prevent="openCropper()"
            class="ml-4 mt-2"
          >
            Crop Images
          </vs-button>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-center mt-5">
        <vs-button
          class="w-full sm:w-48 sm:mr-3"
          type="filled"
          @click="submitForm"
        >
          {{
            formData && formData.purchase_status == 2
              ? "Resubmit"
              : formData
              ? "Update"
              : "Submit"
          }}
        </vs-button>
        <vs-button class="w-full sm:w-48" color="warning">Cancel</vs-button>
      </div>
    </vx-card>

    <!-- Reason Popup Start -->
    <vs-popup
      :title="'Reason'"
      :active.sync="showReasonPopup"
      style="z-index: 99999 !important;"
      @close="closeReasonPopup"
    >
      <div class="text-center">
        <h5>
          {{
            `Reason For ${
              formData && formData.purchase_status == 2
                ? "Resubmit"
                : "Edit/Update"
            }`
          }}
        </h5>
        <div class="vx-row mt-5">
          <div class="vx-col w-full">
            <vs-textarea counter="240" v-model="reason" class="mb-4" rows="4" />
            <vs-button
              color="success"
              type="filled"
              class="mr-3"
              @click="submitForm"
            >
              Submit
            </vs-button>

            <vs-button color="danger" type="filled" @click="closeReasonPopup">
              Cancel
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>
    <!-- Reason Popup End -->

    <!-- Image Cropper Start -->
    <vs-popup
      id="popmodalCrop"
      class="popmodalViewR"
      title="Crop Image"
      :active.sync="popupCropImg"
    >
      <div class="vx-row mt-5">
        <div
          class="vx-col"
          :key="imageKey"
          v-for="(image, imageKey) in allSelectedImages"
        >
          <img
            @click="getImageToCrop(image)"
            v-if="image.dataURL"
            :src="image.dataURL"
            width="150"
            height="150"
          />
        </div>
      </div>
      <!-- SUBMIT BUTTON -->
      <div class="vx-row mt-5">
        <div class="vx-col mx-auto">
          <vue-cropper
            :img-style="{ width: '400px', height: '300px' }"
            v-if="showCropper == true"
            ref="cropper"
            :src="selectedImageForCrop.dataURL"
          >
          </vue-cropper>
          <!-- :disabled="submitStatus" -->
          <vs-button
            @click="getCropedImage"
            color="success"
            type="filled"
            class="mt-3"
            >Crop</vs-button
          >
        </div>
      </div>
    </vs-popup>
    <!-- Image Cropper End -->
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import vue2Dropzone from "vue2-dropzone";
import VueSimpleSuggest from "vue-simple-suggest";

import CategoryService from "@/services/categoryService.js";
import CurrencyService from "@/services/currencyService.js";
import ContractService from "@/services/ContractService.js";
import CountryService from "@/services/countryService.js";
import ExpenseService from "@/services/expenseService.js";
import MethodService from "@/services/methodService.js";
import projectService from "@/services/projectService.js";
import purchaseOrderService from "@/services/purchaseOrderService.js";
import ShootLocationService from "@/services/shootLocationService.js";

import { BASEURL } from "@/config/index";
import constant from "@/helper/constant.js";
import PermissionVariables from "@/services/UserManagementVaraibles.js";
import { debounce, throttle } from "@/helper/utils.js";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  components: { VueSimpleSuggest, Datepicker, vueDropzone: vue2Dropzone },

  props: {
    form_title: { type: String },
    is_var: { type: Number, required: true },
    formData: { type: Object },
  },

  watch: {
    "foreign_remittance.currency": {
      handler: function() {
        if (
          this.foreign_remittance.currency &&
          this.foreign_remittance.currency.currency_id ==
            this.foreign_remittance.converted_currency.currency_id
        ) {
          this.foreign_remittance.currency = null;
          this.$vs.notify({
            title: "Warning!",
            text: "Cannot select INR in From Currency",
            iconPack: "feather",
            icon: "check_box",
            color: "warning",
          });
        }
      },
    },

    "form.gst_amount": {
      handler: function() {
        this.calculateTotal();
      },
    },

    "form.amount": {
      handler: function() {
        if (this.is_var == 5 && this.form.amount > this.vendorFR.FR_balance) {
          this.$vs.notify({
            title: "Warning!",
            text: "Return amount cannot be more than balance FR amount.",
            iconPack: "feather",
            icon: "check_box",
            color: "warning",
          });
        }
      },
    },

    foreign_remittance: {
      handler: function(newVal, oldVal) {
        if (
          !newVal.currency ||
          !newVal.converted_currency ||
          !newVal.unit ||
          !newVal.rate
        ) {
          return;
        }

        this.foreign_remittance.converted_value = newVal.unit * newVal.rate;
        this.foreign_remittance.currency_id =
          newVal.currency.currency_id || null;
        this.foreign_remittance.currency_name =
          newVal.currency.currency_name || null;
        this.foreign_remittance.converted_currency_id =
          newVal.converted_currency.currency_id || null;
        this.foreign_remittance.converted_currency_name =
          newVal.converted_currency.currency_name || null;

        if (this.foreign_remittance.converted_value) {
          this.form.amount = this.foreign_remittance.converted_value;
          this.calculateTotal();
        }
      },
      deep: true,
    },
  },

  data() {
    return {
      form: {
        project: null,
        default_alt_org: null,
        vendor: null,
        invoice_number: null,
        invoice_date: null,
        payment_due_date: null,
        category: null,
        subcategory: null,
        country: null,
        amount: null,
        payment_mode: null,
        priority: null,
        gst: null,
        gst_amount: null,
        total_amount: null,
        shootLocation: null,
        comment: null,
      },

      foreign_remittance: {
        currency: null,
        currency_id: null,
        currency_name: null,
        unit: null,
        rate: null,
        converted_currency: null,
        converted_currency_id: null,
        converted_currency_name: null,
        converted_value: null,
      },

      searchingVendor: false,
      vendorList: [],
      projectList: [],
      currencyList: [],
      shootLocationList: [],
      alternateOrg: [],
      countryList: [],
      categoryList: [],
      subcategoryList: [],
      paymentModeList: [],
      priorityList: [],
      gstList: [],

      popupCropImg: false,
      showCropper: false,
      selectedImageForCrop: null,

      dropzoneOptionsFiles: {
        parallelUploads: 100,
        url: BASEURL + "/v2/multipleUploads?organization=",
        ...constant.DEFAULT_FILES,
        acceptedFiles:
          ".pdf,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.doc,.docx",
        headers: { "My-Awesome-Header": "header value" },
        uploadMultiple: true,
        maxFiles: 100,
        addRemoveLinks: true,
      },
      fileUpload: {
        file_url: "",
        deleteButtonStatus: false,
      },
      uploadedDocument: "-",
      selectedFilesUUID: [],
      allSelectedImages: [],
      uploadedDocumentArray: [],

      organization_id: localStorage.getItem("OrganizationID"),
      permissionPageAccessArry: localStorage.getItem("permissionPageAccess")
        ? localStorage.getItem("permissionPageAccess").split(",")
        : "",
      loginUserRole: window.localStorage.getItem("UserRole"),

      vendorFR: null,

      showReasonPopup: false,
      reason: "",
    };
  },

  methods: {
    getProjectsList: function() {
      this.projectList = [];
      this.alternateOrg = [];
      projectService
        .getAllProjects(null, 1)
        .then((response) => {
          const { data } = response;
          if (data.Status) {
            if (
              !this.permissionPageAccessArry.includes(
                PermissionVariables.ViewOrganizationProjects
              ) &&
              this.loginUserRole != "Admin"
            ) {
              data.data.map((x) => {
                if (
                  this.permissionPageAccessArry.includes("2P" + x.project_id)
                ) {
                  this.projectList.push(x);
                }
              });
            } else {
              this.projectList = data.data;
            }

            if (data.alternateOrgNew.length > 0) {
              this.alternateOrg = data.alternateOrgNew;
            } else {
              this.alternateOrg = [];
            }
          }
        })
        .catch((error) => {
          console.error("Error : ", error.message);
        });
    },

    getCurrencyList: function() {
      CurrencyService.getCurrency()
        .then((response) => {
          if (response.data.Status) {
            this.currencyList = response.data.data;
          }
        })
        .catch((error) => {
          console.error("Error : ", error.message);
        });
    },

    getShootLocationList: function() {
      ShootLocationService.getActiveShootLocation()
        .then((response) => {
          if (response.data.Status) {
            this.shootLocationList = response.data.data;
          }
        })
        .catch((error) => {
          console.error("Error : ", error.message);
        });
    },

    getCountryList: function() {
      CountryService.getCountry()
        .then((response) => {
          if (response.data.Status) {
            this.countryList = response.data.data;
          }
        })
        .catch((error) => {
          console.error("Error : ", error.message);
        });
    },

    getVendorList(search = "") {
      if (!search) return;
      this.vendorList = [];
      this.searchingVendor = true;
      let payload = { search };

      if (this.form.default_alt_org.default_alt_org_id == 0) {
        payload["listType"] = "tv";
      }
      if (this.is_var == 5) {
        payload.forFRR = 1;
      }

      ContractService.getContractVendorNew(payload)
        .then((response) => {
          this.searchingVendor = false;
          if (response.data.Status) {
            this.vendorList = response.data.data;
          }
        })
        .catch((error) => {
          this.searchingVendor = false;
          console.error("Error : ", error.message);
        });
    },

    storePaidFrom() {
      this.form.vendor = null;
    },

    getMasterList: function() {
      MethodService.getAllMaster()
        .then((response) => {
          const { data } = response;
          if (!response.data.Status) {
            return;
          }

          this.priorityList = response.data.data.priority;
          if (this.priorityList.length > 0) {
            this.form.priority = this.priorityList[0];
          }

          this.paymentModeList = response.data.data.payment_mode;
          if (this.paymentModeList.length > 0) {
            this.form.payment_mode = this.paymentModeList[0];
          }
          this.gstList = response.data.data.gst;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    getCategoryList(project_id) {
      CategoryService.getProjectMainCategories(project_id)
        .then((response) => {
          if (response.data.Status) {
            this.categoryList = response.data.data;
            if (this.form.category && this.form.category.category_id) {
              this.getSubcategoryList(
                this.form.category.category_id,
                project_id
              );
            }
          }
        })
        .catch((error) => {
          console.error("Error => ", error.message);
        });
    },

    getSubcategoryList: function(category_id, project_id) {
      CategoryService.getSubCategoriesByParentCategory(category_id, project_id)
        .then((response) => {
          const { data } = response;
          if (data["data"] && data.Status) {
            this.subcategoryList = data.data;
            if (data.data.length == 1) {
              this.form.subcategory = this.subcategoryList[0];
            }
          }
        })
        .catch((error) => {
          console.error("Error => ", error.message);
        });
    },

    projectSelected(project) {
      this.getCategoryList(project.project_id);
    },

    vendorSelected(vendor) {
      this.vendorFR = null;
      if (![4, 5].includes(this.is_var)) return;
      this.getVendorFRBalance(vendor);
    },

    getVendorFRBalance(vendor) {
      let payload = {
        vendor_id: vendor.vendor_id,
        project_id: this.form.project.project_id,
      };

      ExpenseService.getVendorFRBalance(payload)
        .then((response) => {
          if (response.data.Status) {
            this.vendorFR = response.data.data;
          }
        })
        .catch((err) => console.error(err.message));
    },

    categorySelected(category) {
      this.form.subcategory = null;
      this.getSubcategoryList(
        category.category_id,
        this.form.project.project_id
      );
    },

    calculateGST(selectedGST) {
      if (!selectedGST || !this.form.amount) return;
      this.form.gst_amount =
        selectedGST.gst_percentage > 0
          ? Number((this.form.amount * selectedGST.gst_percentage) / 100)
          : 0;
      this.calculateTotal();
    },

    calculateTotal() {
      this.form.total_amount =
        Number(this.form.gst_amount || 0) + Number(this.form.amount || 0);
    },

    vdropzoneSuccessFile: function(file, response) {
      this.uploadedDocumentArray.push(response.data.image_path);
      this.fileUpload.deleteButtonStatus = true;
      this.uploadedDocument = this.uploadedDocumentArray.toString();
      console.log(this.uploadedDocument, "te", this.dupinvoice);
      // this.$refs.myVueDropzoneFile.disable();
    },

    vremoved: function(file) {
      console.log("file, xhr, error :", file.upload);
      let uploaded = this.uploadedDocument.split(",");
      var a = this.selectedFilesUUID.indexOf(file.upload.uuid);

      if (a > -1) {
        uploaded.splice(a, 1);
        this.selectedFilesUUID.splice(a, 1);
        this.uploadedDocumentArray.splice(a, 1);
      }
      this.uploadedDocument = uploaded.toString();
      if (uploaded.length <= 0) {
        this.fileUpload.deleteButtonStatus = false;
        this.uploadedDocument = "-";
        this.uploadedDocumentArray = [];
        this.selectedFilesUUID = [];
      }
    },

    vdropzoneFilesAdded: function(file) {
      console.log("file jitu :", file);
    },

    dataURItoBlob(dataURI) {
      console.log(dataURI, "jj");
      var byteString = atob(dataURI.split(",")[1]);
      var ab = new ArrayBuffer(byteString.length);
      var ia = new Uint8Array(ab);
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: "image/jpeg" });
    },

    getCropedImage: function() {
      let blob = this.$refs.cropper
        .getCroppedCanvas()
        .toDataURL("image/jpeg", 0.9);
      var newFile = this.dataURItoBlob(blob);
      // assign original filename
      newFile.name = this.selectedImageForCrop.name;
      this.vremoved(this.selectedImageForCrop);
      this.$refs.myVueDropzoneFile.removeFile(this.selectedImageForCrop);

      this.$refs.myVueDropzoneFile.addFile(newFile);
    },

    getImageToCrop: function(file) {
      this.selectedImageForCrop = null;
      this.showCropper = false;
      this.selectedImageForCrop = file;

      setTimeout(() => {
        this.showCropper = true;
      }, 500);
    },

    closeCropper: function() {
      this.popupCropImg = false;
    },

    openCropper: function() {
      this.popupCropImg = true;
    },

    clearFile: function() {
      this.$refs.myVueDropzoneFile.removeAllFiles();
      this.fileUpload.deleteButtonStatus = false;
      this.$refs.myVueDropzoneFile.enable();
      this.uploadedDocument = "-";
      this.uploadedDocumentArray = [];
      this.selectedFilesUUID = [];
      this.allSelectedImages = [];
    },

    vdropzoneComplete: function(response) {
      for (let i = 0; i < response.length; i++) {
        this.allSelectedImages.push(response[i]);
        this.selectedFilesUUID.push(response[i].upload.uuid);
      }
      console.log(this.selectedFilesUUID, "hjj");
    },

    vdropzoneError: function(file, message, xhr) {
      console.log("file, message, xhr :", file, message, xhr);
    },

    submitForm() {
      if (this.is_var == 5 && this.form.amount > this.vendorFR.FR_balance) {
        return this.$vs.notify({
          title: "Warning!",
          text: "Return amount cannot be more than balance FR amount.",
          iconPack: "feather",
          icon: "check_box",
          color: "warning",
        });
      }

      if (this.formData && !this.reason) {
        this.showReasonPopup = true;
        return;
      }

      let payload = { ...this.formData };
      this.$vs.loading();
      payload = {
        project_id: this.form.project ? this.form.project.project_id : 0,
        po_name: this.form.vendor ? this.form.vendor.vendor_name : "",
        item_name: "-",
        company_name: "-",
        customFieldValues: [],
        category_id: this.form.subcategory
          ? this.form.subcategory.category_id
          : 0,
        company_address: "-",
        price: this.form.amount,
        currency: this.foreign_remittance.currency.currency_name,
        sgst: "0",
        cgst: "0",
        total: this.form.amount,
        purchase_image: this.uploadedDocument || "-",
        country_id: this.form.country.country_id || 100,
        purchase_description: this.form.comment,
        purchase_status: "0",
        approved_percentage: "0",
        purchase_active: "1",
        location_id: this.form.shootLocation
          ? this.form.shootLocation.location_id
          : 0,
        payment_mode: this.form.payment_mode
          ? this.form.payment_mode.payment_mode_id
          : 0,
        payment_mode_description: "-",
        priority: this.form.priority ? this.form.priority.priority_id : "",
        default_alt_org_id: this.form.default_alt_org
          ? this.form.default_alt_org.default_alt_org_id
          : "",
        purchase_type: "expense",
        approver_id: "0",
        city_id: this.formData ? this.formData.city_id : "-",
        onBehalfId: this.form.onBehalfId ? this.form.onBehalfId.user_id : 0,
        onBehalfName: "",
        isAlreadyPaid: this.isAlreadyPaid ? 1 : 0,
        vendorName: this.form.vendor.vendor_name,
        vendor_id: this.form.vendor.vendor_id,
        invoice_number: this.form.invoice_number,
        payment_due_date: this.form.payment_due_date,
        invoice_date: this.form.invoice_date,
        gst_id: this.form.gst ? this.form.gst.gst_id : 0,
        gst_amount: this.form.gst ? this.form.gst_amount : 0,
        total_amount: this.form.total_amount,
        raised_from_var: 0,
        is_foreign_remittance: 1,
        foreign_remittance: { ...this.foreign_remittance },
        is_var: this.is_var,
        reason: this.reason,
      };

      let action = this.formData
        ? purchaseOrderService.editPurchaseOrder(
            payload,
            this.formData.purchase_id,
            this.formData && this.formData.purchase_status == 2
              ? "resubmit=1"
              : "resubmit=0"
          )
        : ExpenseService.addExpense(payload);

      action
        .then((response) => {
          const { data } = response;
          this.$vs.loading.close();
          if (data.Status == true) {
            if (this.formData) {
              this.closeReasonPopup();
              setTimeout(() => {
                eventBus.$emit("closeEdit");
              }, 300);
            }
            this.$vs.notify({
              title: this.formData ? "Updated!" : "Created!",
              text: data.Message,
              iconPack: "feather",
              icon: "check_box",
              color: "success",
            });
            this.clearForm();
          } else {
            this.$vs.notify({
              title: "Error!",
              text: data.Message,
              iconPack: "feather",
              icon: "check_box",
              color: "warning",
            });
          }
        })
        .catch((error) => {
          this.$vs.notify({
            title: "Error!",
            text: error.message,
            iconPack: "feather",
            icon: "check_box",
            color: "warning",
          });
        });
    },

    clearForm() {
      this.form = {
        project: null,
        default_alt_org: null,
        vendor: null,
        invoice_number: null,
        invoice_date: null,
        payment_due_date: null,
        category: null,
        subcategory: null,
        country: null,
        amount: null,
        payment_mode: null,
        priority: null,
        gst: null,
        gst_amount: null,
        total_amount: null,
        shootLocation: null,
        comment: null,
      };
      this.foreign_remittance = {
        currency: null,
        currency_id: null,
        currency_name: null,
        unit: null,
        rate: null,
        converted_currency: null,
        converted_currency_id: null,
        converted_currency_name: null,
        converted_value: null,
      };
      this.clearFile();
    },

    closeReasonPopup() {
      this.showReasonPopup = false;
    },
  },

  beforeMount() {
    this.getProjectsList();
    this.getCurrencyList();
    this.getShootLocationList();
    this.getCountryList();
    this.getMasterList();

    if (this.formData) {
      this.getCategoryList(this.formData.project_id);
      this.getSubcategoryList(
        this.formData.parent_id,
        this.formData.project_id
      );

      this.form = {
        invoice_number: this.formData.invoice_number || null,
        invoice_date: this.formData.invoice_date || null,
        payment_due_date: this.formData.payment_due_date || null,
        amount: this.formData.total || null,
        gst_amount: this.formData.gst_amount || null,
        total_amount: this.formData.total_amount || null,
        comment: this.formData.purchase_description || null,
      };

      if (this.formData.foreign_remittance) {
        this.foreign_remittance = {
          currency: null,
          currency_id: this.formData.foreign_remittance.currency_id,
          currency_name: this.formData.foreign_remittance.currency_name,
          unit: this.formData.foreign_remittance.unit,
          rate: this.formData.foreign_remittance.rate,
          converted_currency: null,
          converted_currency_id: this.formData.foreign_remittance
            .converted_currency_id,
          converted_currency_name: this.formData.foreign_remittance
            .converted_currency_name,
          converted_value: this.formData.foreign_remittance.converted_value,
        };
      }

      setTimeout(() => {
        this.form.project = this.projectList.filter(
          (project) => project.project_id == this.formData.project_id
        )[0];

        this.form.default_alt_org = this.alternateOrg.filter(
          (org) => org.default_alt_org_id == this.formData.amount_paid_from
        )[0];

        this.form.vendor = {
          vendor_id: this.formData.vendor_id,
          vendor_name: this.formData.vendor_name,
        };
        this.vendorList = [this.form.vendor];

        this.form.category = this.categoryList.filter(
          (cat) => cat.category_id == this.formData.parent_id
        )[0];

        this.form.subcategory = this.subcategoryList.filter(
          (cat) => cat.category_id == this.formData.category_id
        )[0];

        this.form.gst = this.gstList.filter(
          (gst) => gst.gst_id == this.formData.gst_id
        )[0];

        this.form.country = this.countryList.filter(
          (country) => country.country_id == this.formData.country_id
        )[0];

        this.form.payment_mode = this.paymentModeList.filter(
          (payment) => payment.payment_mode_id == this.formData.payment_mode
        )[0];

        this.form.priority = this.priorityList.filter(
          (priority) => priority.priority_id == this.formData.priority
        )[0];

        this.form.shootLocation = this.shootLocationList.filter(
          (loc) => loc.location_id == this.formData.location_id
        )[0];

        this.foreign_remittance.currency = this.currencyList.filter(
          (currency) =>
            currency.currency_id == this.formData.foreign_remittance.currency_id
        )[0];

        if (this.formData.gst_id > 0) {
          this.form.gst = this.gstList.filter(
            (gst) => gst.gst_id == this.formData.gst_id
          )[0];
        }
      }, 5000);
    }
    setTimeout(() => {
      this.foreign_remittance = this.formData
        ? this.formData.foreign_remittance
        : {};

      this.foreign_remittance.converted_currency = this.currencyList.filter(
        (item) => item.currency_id === 65
      )[0];

      if (!this.formData) {
        this.form.gst = this.gstList.filter(
          (gst) => gst.gst_name == "Manual Amount"
        )[0];
      }

      this.form.default_alt_org = this.alternateOrg[0];
    }, 2000);
  },

  created() {
    this.vendorSearched = debounce((event) => {
      this.getVendorList(event);
    }, 1000);
  },
};
</script>
<style>
.heading h4 {
  font-family: inherit;
  font-weight: bolder;
  line-height: 1.2;
  font-size: 33px;
  font-variant: all-small-caps;
  color: rgba(var(--vs-primary), 1);
}
</style>
